import { State, MainThunkDispatch } from "../../Reducers"
import { APIEndpointInfo } from "../../Routes/api"
import { NetworkAction } from "../../Reducers/Client/NetworkReducer"
import { NetworkType } from "../Types"
import { safePromise } from "../../Helpers/Functions/promise"
import {
    getApiKey,
    isSignedOutOrSigningOut
} from "../../Helpers/Functions/Selectors/auth"
import { getValidSession } from "../Auth/AuthActions"
import { config } from "bridger"
import { BadRequest } from "../../Models/Client/BadRequest"

const POLL_MIN_LIMIT = 1000
const DEBOUNCE = 200

export const killPoll = (id: string) => {
    const action: NetworkAction = {
        type: NetworkType.setPollState,
        payload: {
            id,
            poll: {
                startTimestamp: undefined
            }
        }
    }

    return action
}

export const pollAPI =
    <M = any>(
        id: string,
        info: APIEndpointInfo,
        onResponse: (response: M | undefined) => void,
        onError?: (response: Response) => void,
        pollInterval: number = POLL_MIN_LIMIT,
        logError: boolean = true
    ) =>
    (dispatch: MainThunkDispatch, getState: () => State) => {
        const getActivePollStartTimestamp = () => {
            const poll = getState().client.network.poll[id]

            return poll === undefined ? undefined : poll.startTimestamp
        }

        if (getActivePollStartTimestamp() !== undefined) {
            console.error("Poll exists.")
            return
        }

        const startTimestamp = +new Date()

        dispatch({
            type: NetworkType.setPollState,
            payload: {
                id,
                poll: {
                    startTimestamp
                }
            }
        })

        const interval =
            pollInterval < POLL_MIN_LIMIT ? POLL_MIN_LIMIT : pollInterval
        const fn = async (timestamp: number) => {
            if (getActivePollStartTimestamp() !== timestamp) return

            const { data, error } = await safePromise(
                dispatch(
                    apiFetchResponse(id, info, undefined, undefined, !logError)
                )
            )

            if (getActivePollStartTimestamp() !== timestamp) return

            setTimeout(() => fn(timestamp), interval)

            if (error !== undefined) {
                console.warn(`Polling: received an error: ${error.message}`)
                return
            }

            if (data?.ok === false) {
                onError?.(data)
                return
            }

            if (data?.status === 204) {
                onResponse(undefined)
            } else {
                onResponse(await data?.json())
            }
        }
        fn(startTimestamp)
    }

/**
 * require api key by default
 * get api url
 * call fetch fn
 *
 * @param id used in the debouncer
 * @param info provided by apiEndpoints function
 * @param options (optional) additional options for api call
 * @param debounceInterval (optional) how long to wait before allowing next request
 */
export const apiFetch =
    <M = any>(
        id: string,
        info: APIEndpointInfo,
        options?: RequestInit,
        debounceInterval: number = DEBOUNCE,
        dontParse: boolean = false,
        ignoreError: boolean = false
    ) =>
    async (dispatch: MainThunkDispatch): Promise<M | undefined> => {
        const res = await dispatch(
            apiFetchResponse(id, info, options, debounceInterval, ignoreError)
        )

        if (res === undefined) {
            return
        }

        if (!res.ok) {
            const body = await res.json()
            let message =
                typeof body === "object" && body !== null
                    ? (body as any).message
                    : undefined

            // we expect that API might return some details about the error in "detail" property in reponse.
            // format of detail is FastAPI validation error (loc, msg, type)
            if (
                res.status === 422 &&
                typeof body === "object" &&
                body !== null &&
                body.detail
            ) {
                const detail_str = body.detail
                    .map((item: any) => item.msg)
                    .join(", ")
                message = `${message} (${detail_str})`
            }

            if (
                res.status >= 400 &&
                res.status < 500 &&
                message !== undefined
            ) {
                throw new BadRequest(message)
            }

            throw new Error(message ?? "unknown error")
        }

        if (dontParse || res.status === 204) {
            return res as any
        }

        return res.json()
    }

export const apiFetchResponse =
    (
        id: string,
        info: APIEndpointInfo,
        options?: RequestInit,
        debounceInterval: number = DEBOUNCE,
        ignoreError: boolean = false
    ) =>
    async (
        dispatch: MainThunkDispatch,
        getState: () => State
    ): Promise<Response | undefined> => {
        const requireApiKey =
            !info.useAPIKeyIfAvailable && !info.dontRequireAPIKey

        if (
            isSignedOutOrSigningOut(getState()) &&
            (requireApiKey || info.requireAuthToken)
        ) {
            return
        }

        const state = getState()
        const apiKey = getApiKey(state)
        if (requireApiKey && !apiKey) return Promise.reject("API key required.")

        const apiKeyHeader = (info.useAPIKeyIfAvailable ||
            !info.dontRequireAPIKey) &&
            apiKey && {
                "API-KEY": apiKey
            }

        let authToken
        if (info.requireAuthToken) {
            const token = await safePromise(dispatch(getValidSession()))
            if (token.error !== undefined) throw token.error
            if (token.data === undefined)
                return Promise.reject("Auth token required.")
            authToken = token.data
        }

        const authTokenHeader = info.requireAuthToken &&
            authToken && {
                "AUTH-TOKEN": authToken
            }

        const contentTypeHeader = options !== undefined &&
            options.body !== undefined && {
                "Content-Type": "application/json"
            }

        const baseOptions: RequestInit = {
            headers: {
                ...contentTypeHeader,
                ...apiKeyHeader,
                ...authTokenHeader
            } as Record<string, string>
        }

        const apiUrl =
            info.service === "autoSegmentation"
                ? config.autoSegmentationService
                : info.service === "objectTracking"
                ? config.objectTrackingService
                : info.service === "clientPulse"
                ? config.clientPulseUrl
                : state.client.endpoints.apiUrl
        const url = `${apiUrl}${info.url}`

        const safeFetchOptions = { ...baseOptions, ...options }

        try {
            return await dispatch(
                safeFetch(id, url, safeFetchOptions, debounceInterval)
            )
        } catch (error) {
            if (!ignoreError) {
                console.error("API call error", url, safeFetchOptions, error)
            }
            throw error
        }
    }

/**
 * don't fetch an identical fetch in process or within 1s of return
 * check fetch response
 * cast to json by default
 *
 * @param id Identifier for request to allow effective debouncing
 * @param url The URL endpoint to fetch
 * @param options Request options for the endpoint (headers, body)
 * @param debounceInterval (optional) how long to wait before allowing next request
 */
export const safeFetch =
    (
        id: string,
        url: string,
        options: RequestInit,
        debounceInterval: number = DEBOUNCE
    ) =>
    async (
        dispatch: MainThunkDispatch,
        getState: () => State
    ): Promise<Response | undefined> => {
        const debounce = getState().client.network.debounce[id]
        const now = Date.now()

        if (
            debounce !== undefined &&
            (debounce.inProgress ||
                debounce.previousTime + debounceInterval > now)
        ) {
            return
        }

        dispatch({
            type: NetworkType.setDebounceState,
            payload: {
                id,
                debounce: {
                    interval: debounceInterval,
                    inProgress: true,
                    previousTime: now
                }
            }
        })

        try {
            let retries = 0

            while (true) {
                const res = await fetch(url, options)
                const shouldRetry =
                    retries <= 3 &&
                    (options.method ?? "get").toLowerCase() === "get" &&
                    res.status >= 500

                if (!shouldRetry) {
                    return res
                }

                retries += 1

                const timeout = 1000 * Math.pow(2, retries)

                console.log(`Retry #${retries} ${url}`)
                await new Promise(r => setTimeout(r, timeout))
            }
        } finally {
            dispatch({
                type: NetworkType.setDebounceState,
                payload: {
                    id,
                    debounce: {
                        inProgress: false,
                        previousTime: Date.now(),
                        interval: debounceInterval
                    }
                }
            })
        }
    }
